import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, ErrorState, Modal, Nota, Skeleton } from '../../../ui';
import PainelExecucaoSync from '../../ti/PainelExecucaoSync';
import { extrairMensagemErro } from '../../../utils/apiError';
import {
  ProdutoSyncStatus,
  getProdutoSyncStatus,
  sincronizarProdutos,
} from '../../../api/sga-produto/sga-produtoApi';

/**
 * Sincronização do espelho de produtos + flags de obrigatoriedade (at-04).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 🔴 **A correção que importa: a falha da leitura deixou de ser um toast a cada 3
 * segundos.** O polling roda enquanto o modal está aberto, e o `catch` do
 * `carregar()` fazia `toast.error` — com o backend fora, eram ~20 toasts por
 * minuto, empilhados, enquanto o corpo do modal ficava **em branco** (só os
 * botões), sem dizer que a leitura tinha falhado. Agora a falha vira **um**
 * estado na tela, e a distinção segue a regra do `13-ui-kit.md`:
 *
 *   • **nunca houve leitura** → `ErrorState` (não há conteúdo; oferece tentar de novo);
 *   • **havia leitura e a atualização falhou** → `Nota atencao` (o conteúdo está
 *     lá, só pode estar velho) — e ela some sozinha quando a leitura voltar,
 *     porque o polling continua.
 *
 * ⚠️ **O polling de 3 s enquanto aberto NÃO mudou** (decisão D10 do plano): é o
 * que o manual do TI promete ao dizer *"o progresso acima se atualiza sozinho"*.
 */

const POLLING_MS = 3000;

const FASE_LABEL: Record<string, string> = {
  ESPELHAMENTO: 'espelhando produtos do SGA',
  FLAGS: 'descobrindo obrigatoriedade',
};

interface ModalSincronizarProdutosProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalSincronizarProdutos: React.FC<ModalSincronizarProdutosProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<ProdutoSyncStatus | null>(null);
  const [falhaLeitura, setFalhaLeitura] = useState('');
  const [disparando, setDisparando] = useState(false);

  /* Sem `status` no array de dependências do efeito: `carregar` não pode mudar a
     cada resposta, senão o `setInterval` é recriado a cada 3 s. */
  const jaLeu = useRef(false);

  const emAndamento = status?.execucao?.status === 'EM_ANDAMENTO';

  const carregar = useCallback(async () => {
    try {
      setStatus(await getProdutoSyncStatus());
      jaLeu.current = true;
      setFalhaLeitura('');
    } catch (falha) {
      setFalhaLeitura(extrairMensagemErro(falha, 'Não foi possível consultar o estado agora.'));
    }
  }, []);

  // Carrega ao abrir e faz polling enquanto o modal estiver aberto.
  useEffect(() => {
    if (!isOpen) return undefined;

    jaLeu.current = false;
    setFalhaLeitura('');
    void carregar();

    const intervalo = setInterval(() => {
      void carregar();
    }, POLLING_MS);

    return () => clearInterval(intervalo);
  }, [isOpen, carregar]);

  const handleDisparar = async () => {
    setDisparando(true);
    try {
      const resultado = await sincronizarProdutos();
      toast.success(resultado.mensagem);
      await carregar();
    } catch (falha) {
      // ⚠️ Aqui o toast FICA: foi o TI que clicou, e ele precisa saber que o
      // clique dele não funcionou. É diferente da leitura de fundo.
      toast.error(extrairMensagemErro(falha, 'Erro ao disparar sincronização'));
    } finally {
      setDisparando(false);
    }
  };

  const execucao = status?.execucao;
  const semLeituraNenhuma = !status && !!falhaLeitura;

  return (
    <Modal
      aberto={isOpen}
      titulo="Sincronizar Produtos (SGA)"
      ocupado={disparando}
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose} disabled={disparando}>
          Fechar
        </Botao>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!status && !falhaLeitura && <Skeleton linhas={2} altura="tw-h-12" />}

        {semLeituraNenhuma && (
          <ErrorState
            mensagem="Não foi possível consultar o estado da sincronização."
            detalhe={falhaLeitura}
            aoTentarNovamente={() => void carregar()}
          />
        )}

        {status && (
          <>
            {falhaLeitura && (
              <Nota variante="atencao">
                Não foi possível atualizar o estado agora — o que está abaixo é da última leitura
                que deu certo. A tela tenta de novo sozinha a cada poucos segundos.
              </Nota>
            )}

            <p className="tw-m-0 tw-text-sm tw-text-texto-corpo">
              <b>Produtos locais:</b> {status.totalProdutos} ({status.produtosComFlagDescoberta} com
              obrigatoriedade descoberta)
            </p>

            {execucao && (
              <PainelExecucaoSync
                status={execucao.status}
                faseRotulo={FASE_LABEL[execucao.fase] ?? execucao.fase}
                iniciadoEm={execucao.iniciadoEm}
                percentual={execucao.percentual}
                progresso={{ feitas: execucao.processados, total: execucao.total }}
                erro={execucao.erro}
                resumo={
                  <>
                    {execucao.sincronizadas} espelhados · flags: {execucao.flagsCobertos}{' '}
                    descobertas · {execucao.flagsSemPerfil} sem perfil · {execucao.flagsFalhas}{' '}
                    falhas · {execucao.flagsChamadas} consultas
                  </>
                }
              />
            )}
          </>
        )}

        <Botao
          variante="primario"
          larguraTotal
          carregando={disparando}
          disabled={emAndamento}
          onClick={handleDisparar}
        >
          {emAndamento
            ? 'Sincronização em andamento...'
            : disparando
              ? 'Disparando...'
              : 'Sincronizar agora'}
        </Botao>

        <Nota>
          Espelha os produtos ativos do SGA e descobre as flags de obrigatoriedade
          (compulsório/padrão). Roda em segundo plano — pode fechar este menu; o progresso acima se
          atualiza sozinho.
        </Nota>
      </div>
    </Modal>
  );
};

export default ModalSincronizarProdutos;
